/**
 * Structured Data (Schema.org) Utilities
 *
 * Generates JSON-LD structured data for SEO
 */

import { Fair, Vendor, VendorPublicProfile } from '../types';

/**
 * Generate Organization schema for homepage
 */
export const getOrganizationSchema = () => {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Local Artist Fairs',
    description: 'Discover local artist fairs, craft shows, and artisan markets. Find upcoming events, browse vendors, and book vendor booth spaces.',
    url: window.location.origin,
    logo: `${window.location.origin}/logo.png`,
    sameAs: [
      // Add social media links when available
    ],
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'Customer Service',
      email: 'localartistfairs@gmail.com'
    }
  };
};

/**
 * Generate Event schema for a fair
 */
export const getFairEventSchema = (fair: Fair) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: fair.name,
    description: fair.description || `Join us at ${fair.name}`,
    startDate: fair.start_date,
    endDate: fair.end_date,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    location: {
      '@type': 'Place',
      name: fair.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: fair.address,
        addressLocality: fair.city,
        addressRegion: fair.state,
        addressCountry: 'US'
      }
    }
  };

  // Add image if available
  if (fair.images && fair.images.length > 0) {
    schema.image = fair.images[0].image_url;
  }

  // Add organizer
  schema.organizer = {
    '@type': 'Organization',
    name: 'Local Artist Fairs',
    url: window.location.origin
  };

  // Add offers if vendor booth slots are available
  if (fair.available_slots && fair.available_slots > 0) {
    schema.offers = {
      '@type': 'Offer',
      availability: 'https://schema.org/InStock',
      url: `${window.location.origin}/fairs/${fair.id}/slots`,
      priceCurrency: 'USD',
      description: `${fair.available_slots} vendor booth spaces available`
    };
  }

  return schema;
};

/**
 * Generate LocalBusiness schema for a vendor
 */
export const getVendorBusinessSchema = (vendor: Vendor | VendorPublicProfile) => {
  const schema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: vendor.business_name,
    description: vendor.description || '',
  };

  // Add image if available
  if (vendor.portfolio_images && vendor.portfolio_images.length > 0) {
    schema.image = vendor.portfolio_images[0];
  }

  // Add website if available
  if (vendor.website) {
    schema.url = vendor.website;
  }

  // Add social media links
  const sameAs = [];
  if (vendor.social_media_instagram) sameAs.push(vendor.social_media_instagram);
  if (vendor.social_media_facebook) sameAs.push(vendor.social_media_facebook);
  if (vendor.social_media_twitter) sameAs.push(vendor.social_media_twitter);
  if (sameAs.length > 0) {
    schema.sameAs = sameAs;
  }

  return schema;
};

/**
 * Generate BreadcrumbList schema for navigation
 */
export const getBreadcrumbSchema = (breadcrumbs: Array<{ name: string; url: string }>) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: breadcrumbs.map((crumb, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: crumb.name,
      item: crumb.url
    }))
  };
};

/**
 * Generate ItemList schema for fair listings
 */
export const getFairListSchema = (fairs: Fair[]) => {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: fairs.map((fair, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${window.location.origin}/fair/${fair.id}`
    }))
  };
};
